/*
 * cart-upgrades.js
 *
 * Drives two cart-drawer features that are rendered by Liquid but need JS.
 * The theme's own CartItems class only re-renders [data-line-items] + subtotal;
 * it never touches the shipping bar or the upsell cards, so this file fills the gap.
 *
 *   1. Free shipping progress bar  (snippets/free-shipping-bar.liquid)
 *      - Converts the base-currency threshold to the active presentment currency
 *        using window.Shopify.currency.rate (the same first-party rate the stock
 *        theme uses — no external currency API).
 *      - Re-syncs copy / meta / fill / state classes on every cart mutation via
 *        the theme's `theme:cart:change` event.
 *
 *   2. Upsell "+ Add" buttons  (snippets/cart-upsell-card.liquid)
 *      - POSTs to /cart/add.js, then fires the theme's cart events so the new line
 *        item, subtotal, total and shipping bar all update in place.
 */

(function () {
  'use strict';

  var SELECTORS = {
    bar: '[data-shipping-bar]',
    copy: '[data-shipping-copy]',
    meta: '[data-shipping-meta]',
    fill: '[data-shipping-fill]',
    upsellAdd: '[data-upsell-add]',
    upsellCard: '[data-upsell-card]',
    errors: '[data-form-errors]'
  };

  var CLASSES = {
    hit: 'osi-shipping-bar--hit',
    subscription: 'osi-shipping-bar--subscription'
  };

  // Latest known cart, kept in sync via the theme's cart:change event.
  var latestCart = null;

  /* ----------------------------------------------------------------------- *
   * Money formatting — mirror the Liquid display: round to the nearest whole
   * major unit, group thousands, prefix the presentment currency symbol.
   * e.g. 1340000 -> "₱13,400"
   * ----------------------------------------------------------------------- */
  function formatAmount(minorUnits, symbol) {
    var major = Math.round((Number(minorUnits) || 0) / 100);
    return (symbol || '') + major.toLocaleString('en-US');
  }

  /* ----------------------------------------------------------------------- *
   * Threshold: convert base-currency minor units -> presentment minor units.
   * Shopify exposes the base->presentment multiplier at window.Shopify.currency.rate.
   * When base === presentment (or the rate is missing) the rate is 1, so the
   * threshold stays in base units.
   * ----------------------------------------------------------------------- */
  function getRate() {
    var rate =
      window.Shopify &&
      window.Shopify.currency &&
      parseFloat(window.Shopify.currency.rate);
    return rate && rate > 0 ? rate : 1;
  }

  function refreshThreshold(bar) {
    var base = parseInt(bar.getAttribute('data-base-threshold'), 10) || 0;
    if (!base) return;
    var threshold = Math.round(base * getRate());
    bar.setAttribute('data-threshold', threshold);
    bar.setAttribute(
      'data-threshold-display',
      formatAmount(threshold, bar.getAttribute('data-currency-symbol'))
    );
  }

  /* ----------------------------------------------------------------------- *
   * Shipping bar sync
   * ----------------------------------------------------------------------- */
  function cartHasSubscription(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(function (item) {
      return !!(item.selling_plan_allocation || item.selling_plan);
    });
  }

  function updateShippingBar(cart) {
    var bar = document.querySelector(SELECTORS.bar);
    if (!bar) return;

    var copy = bar.querySelector(SELECTORS.copy);
    var meta = bar.querySelector(SELECTORS.meta);
    var fill = bar.querySelector(SELECTORS.fill);

    var threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
    var symbol = bar.getAttribute('data-currency-symbol') || '';
    var thresholdDisplay =
      bar.getAttribute('data-threshold-display') || formatAmount(threshold, symbol);

    var cartTotal = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;

    // ---- Subscription state: free shipping always included ----
    if (cartHasSubscription(cart)) {
      bar.classList.add(CLASSES.hit, CLASSES.subscription);
      bar.setAttribute('data-has-subscription', 'true');
      if (copy) copy.textContent = '✓ Free shipping included with your subscription';
      if (meta) meta.style.display = 'none';
      if (fill) fill.style.width = '100%';
      return;
    }

    bar.classList.remove(CLASSES.subscription);
    bar.setAttribute('data-has-subscription', 'false');
    if (meta) meta.style.display = '';

    var remaining = threshold - cartTotal;
    if (remaining < 0) remaining = 0;

    var progress = threshold > 0 ? (cartTotal * 100) / threshold : 0;
    if (progress > 100) progress = 100;

    var hit = threshold > 0 && cartTotal >= threshold;
    bar.classList.toggle(CLASSES.hit, hit);

    if (copy) {
      if (hit) {
        copy.innerHTML = '🎉 Congrats! You\'ve unlocked <strong>FREE SHIPPING!</strong>';
      } else {
        copy.textContent = formatAmount(remaining, symbol) + ' away from free shipping';
      }
    }

    if (meta) {
      meta.textContent = formatAmount(cartTotal, symbol) + ' / ' + thresholdDisplay;
    }

    if (fill) {
      fill.style.width = progress + '%';
    }
  }

  function syncBar(cart) {
    var bar = document.querySelector(SELECTORS.bar);
    if (!bar) return;
    refreshThreshold(bar);
    updateShippingBar(cart || latestCart);
  }

  /* ----------------------------------------------------------------------- *
   * Upsell "+ Add"
   * ----------------------------------------------------------------------- */
  function fireCartChange(cart) {
    document.dispatchEvent(
      new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true })
    );
  }

  function fireCartReload() {
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
  }

  function showError(message) {
    var errors = document.querySelector(SELECTORS.errors);
    if (errors) {
      errors.innerHTML = '<p>' + message + '</p>';
      errors.style.display = '';
    }
  }

  function handleUpsellAdd(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;

    var originalLabel = button.innerHTML;
    button.disabled = true;
    button.innerHTML = 'Adding…';

    window
      .fetch(window.theme.routes.cart + '/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw err;
          });
        }
        // Re-fetch the full cart so totals / subtotal / shipping bar are accurate.
        return window.fetch(window.theme.routes.cart + '.js').then(function (r) {
          return r.json();
        });
      })
      .then(function (cart) {
        latestCart = cart;
        fireCartChange(cart); // updates subtotal/total and (via our listener) the bar
        fireCartReload(); // re-renders [data-line-items] with the new product
        syncBar(cart);

        // Drop the just-added product's card from the upsell list.
        var card = button.closest(SELECTORS.upsellCard);
        if (card) card.remove();
      })
      .catch(function (err) {
        console.error('[cart-upgrades] add to cart failed:', err);
        button.disabled = false;
        button.innerHTML = originalLabel;
        showError((err && (err.description || err.message)) || 'Could not add to cart');
      });
  }

  /* ----------------------------------------------------------------------- *
   * Wiring
   * ----------------------------------------------------------------------- */
  function init() {
    // Delegated click handler survives drawer re-renders of the upsell list.
    document.addEventListener('click', function (event) {
      var button = event.target.closest(SELECTORS.upsellAdd);
      if (button) {
        event.preventDefault();
        handleUpsellAdd(button);
      }
    });

    // Re-sync the bar on every cart mutation the theme reports.
    document.addEventListener('theme:cart:change', function (event) {
      if (event.detail && event.detail.cart) {
        latestCart = event.detail.cart;
      }
      syncBar(latestCart);
    });

    // Initial sync — don't rely solely on the theme's load-time event.
    window
      .fetch(window.theme.routes.cart + '.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        latestCart = cart;
        syncBar(cart);
      })
      .catch(function () {
        syncBar(null);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
